<template>
  <CurrentEventDataProvider v-slot="{ hasFullEventDetails, data, features }">
    <div class="lobby" v-if="hasFullEventDetails" id="main-content" role="main">
      <!-- Navbar -->
      <Navbar :show-nav="true" :full-width="true" />

      <div
        class="lobby-wrapper overflow-x-hidden"
        style="background-color: var(--background-color, #f3f4f6)"
      >
        <!-- Hero banner stays pinned at the top. -->
        <HeroBanner :data="data" :features="features" />

        <!-- Content sections — rendered in the EXACT order the admin
             arranged them in the lobby builder (driven by lobbyElements
             order). "Join the Community" is excluded here and always
             rendered last below. -->
        <component
          v-for="section in orderedSections"
          :is="section.component"
          :key="section.key"
          v-bind="section.props"
        />

        <!-- Join the Community — always last. Shown only when the admin's
             Participants toggle is on, and titled with the admin's
             "Participants label". -->
        <JoinCommunity
          v-if="isJoinCommunityEnabled"
          :label="participantsLabel"
        />

        <BackToTop />
      </div>
    </div>
    <div v-else class="full-page-loader">
      <img
        src="/assets/favicon/apple-touch-icon.png"
        :alt="$t('Views.Lobby.tappinLogo')"
      />
      {{ $t('Views.Lobby.loading') }}
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { mapGetters } from 'vuex';
import Navbar from '@/components/shared/Navbar';
import Banner from '@/components/Banner';
import LobbySponsorTopList from '@/views/Lobby/LobbySponsorTopList';
import LobbySponsors from '@/views/Lobby/LobbySponsors';
import LobbyAgenda from '@/views/Lobby/LobbyAgenda';
import LobbyFaq from '@/views/Lobby/LobbyFaq';
import LobbySpeakers from '@/views/Lobby/LobbySpeakers';
import BackToTop from '@/components/shared/BackToTop';
import LobbyFooter from '@/views/Lobby/LobbyFooter';
import HeroBanner from '@/views/Lobby/HeroBanner';
import EventInfoCards from '@/views/Lobby/EventInfoCards';
import EventBadge from '@/views/Lobby/EventBadge';
import VoucherBalance from '@/components/profile/VoucherBalance';
import JoinCommunity from '@/views/Lobby/JoinCommunity';
import SponsorsMarquee from '@/views/Lobby/SponsorsMarquee';
import {
  lobbyElementCategory,
  moduleType
} from '@/constants/lobby-page-constants';
import { isPhysicalAttendee } from '@/helpers/redirection-helpers';

const componentList = [
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.BANNER,
    component: Banner
  },
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.INFO_PAGE,
    component: LobbyFaq
  },
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.AGENDA,
    component: LobbyAgenda
  },
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.SPEAKER,
    component: LobbySpeakers
  },
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.SPONSOR,
    component: LobbySponsors
  },
  {
    category: lobbyElementCategory.MODULE,
    type: moduleType.TOP_SPONSORS,
    component: LobbySponsorTopList
  }
];

export default {
  name: 'Lobby',
  components: {
    BackToTop,
    LobbySponsorTopList,
    LobbySponsors,
    LobbyAgenda,
    LobbyFaq,
    LobbySpeakers,
    Navbar,
    Banner,
    LobbyFooter,
    HeroBanner,
    EventInfoCards,
    EventBadge,
    VoucherBalance,
    SponsorsMarquee,
    JoinCommunity
  },
  computed: {
    ...mapGetters('CurrentEvent', [
      'isLobbyEnabled',
      'lobbyElements',
      'hasFullEventDetails',
      'features'
    ]),
    ...mapGetters('Authentication', { profile: 'getUser' }),
    // Physical attendees must never see the lobby, even when the admin has
    // it enabled. Drives the guard below (mirrors the redirect helper).
    isPhysicalAttendee() {
      return isPhysicalAttendee();
    },
    hasFaqElement() {
      if (!this.lobbyElements) return false;
      return this.lobbyElements.some((x) => x.id === 'information');
    },
    faqOptions() {
      return (
        this.lobbyElements?.find((x) => x.id === 'information')?.options ?? {}
      );
    },
    hasSpeakersElement() {
      if (!this.lobbyElements) return false;
      return this.lobbyElements.some((x) => x.id === 'speakers');
    },
    speakersOptions() {
      return (
        this.lobbyElements?.find((x) => x.id === 'speakers')?.options ?? {}
      );
    },
    hasSponsorsElement() {
      if (!this.lobbyElements) return false;
      return this.lobbyElements.some(
        (x) =>
          x.id === 'sponsors' ||
          x.id === 'topSponsors' ||
          x.type === moduleType.SPONSOR ||
          x.type === moduleType.TOP_SPONSORS
      );
    },
    // The categories the admin defined as sponsor sections in the lobby
    // builder ("Select category" → `options.dropdown`), in admin order.
    // Each becomes a labelled group on the platform.
    sponsorCategoryFilters() {
      if (!Array.isArray(this.lobbyElements)) return [];
      const out = [];
      this.lobbyElements.forEach((x) => {
        if (!x) return;
        const isSponsor = x.id === 'sponsors' || x.type === moduleType.SPONSOR;
        if (!isSponsor) return;
        const d = x.options && x.options.dropdown;
        if (d !== undefined && d !== null && String(d).trim() !== '') {
          out.push(d);
        }
      });
      return out;
    },
    // True when the admin has at least one sponsor section WITHOUT a
    // selected category — i.e. a catch-all "show all sponsors" section.
    // Drives whether uncategorized sponsors are shown on the platform.
    hasUncategorizedSponsorSection() {
      if (!Array.isArray(this.lobbyElements)) return true;
      const sponsorEls = this.lobbyElements.filter(
        (x) => x && (x.id === 'sponsors' || x.type === moduleType.SPONSOR)
      );
      if (!sponsorEls.length) return true;
      return sponsorEls.some((x) => {
        const d = x.options && x.options.dropdown;
        return d === undefined || d === null || String(d).trim() === '';
      });
    },
    // The admin's heading for the sponsor section ("Label" in the lobby
    // builder, e.g. "MNO Typed Sponsors"). Falls back to the first
    // sponsor element with a label so the platform mirrors admin.
    sponsorsSectionLabel() {
      if (!Array.isArray(this.lobbyElements)) return '';
      const el = this.lobbyElements.find(
        (x) =>
          x &&
          (x.id === 'sponsors' || x.type === moduleType.SPONSOR) &&
          x.options &&
          x.options.label &&
          String(x.options.label).trim() !== ''
      );
      return (el && el.options.label) || '';
    },
    isParticipantsEnabled() {
      if (!this.lobbyElements) return false;
      return (
        this.lobbyElements.find((x) => x.id === 'header')?.options
          ?.participantsEnabled ?? false
      );
    },
    // The admin "Participants" header element (carries the toggle and
    // the custom label for the "Join the Community" section).
    participantsHeaderOptions() {
      if (!Array.isArray(this.lobbyElements)) return null;
      const header = this.lobbyElements.find((x) => x && x.id === 'header');
      return (header && header.options) || null;
    },
    // "Join the Community" shows only when the admin's Participants
    // toggle is on. When the lobby builder isn't configured (no header
    // element), fall back to the participants feature flag so older
    // events still behave.
    isJoinCommunityEnabled() {
      const o = this.participantsHeaderOptions;
      if (o && 'participantsEnabled' in o) {
        return !!o.participantsEnabled;
      }
      return this.isParticipantsFeatureEnabled;
    },
    // The section heading — the admin's "Participants label", falling
    // back to the default when none is set.
    participantsLabel() {
      const o = this.participantsHeaderOptions;
      const label = o && o.participantsLabel;
      return (
        (label && String(label).trim()) ||
        this.$t('Views.Lobby.joinTheCommunity')
      );
    },
    isParticipantsFeatureEnabled() {
      // The admin "Participants list" feature flag — falls back to true
      // when the feature is undefined so we don't break events that
      // were created before the flag existed.
      const f = this.features || {};
      if (
        f.lobbyParticipantList === false ||
        f.stageTabParticipants === false
      ) {
        // If both webapp/lobby participants flags are explicitly off, hide.
        if (
          f.lobbyParticipantList === false &&
          f.stageTabParticipants === false
        ) {
          return false;
        }
      }
      // Default: show unless explicitly disabled
      return (
        f.lobbyParticipantList !== false || f.stageTabParticipants !== false
      );
    },
    isQrOrVoucherEnabled() {
      const qrEnabled = !!(
        this.profile &&
        this.profile.TicketQrCode &&
        this.features?.stageQrTicketsEnabled
      );
      const voucherEnabled = !!(
        this.profile && this.features?.stageVoucherBalanceEnabled
      );
      return qrEnabled || voucherEnabled;
    },
    // Content sections in the exact order the admin arranged them in the
    // lobby builder. The hero banner is rendered separately at the top
    // and "Join the Community" separately at the very bottom, so neither
    // appears here. Regular sponsor modules collapse into one unified
    // SponsorsMarquee at the first sponsor element's position (the
    // admin's category sections become labelled groups inside it); each
    // Top sponsors module renders as its own section where it was placed.
    orderedSections() {
      const f = this.features || {};
      // No admin lobby config → fall back to the default content order.
      if (!this.lobbyElements || !this.lobbyElements.length) {
        const out = [];
        if (this.hasFaqElement && f.lobbyFAQPages !== false) {
          out.push({
            key: 'information',
            component: 'LobbyFaq',
            props: { options: this.faqOptions }
          });
        }
        if (this.hasSpeakersElement && f.lobbySpeakers !== false) {
          out.push({
            key: 'speakers',
            component: 'LobbySpeakers',
            props: { options: this.speakersOptions }
          });
        }
        if (this.hasSponsorsElement && f.lobbySponsors !== false) {
          out.push({
            key: 'sponsors',
            component: 'SponsorsMarquee',
            props: {
              label: this.sponsorsSectionLabel,
              categoryFilters: this.sponsorCategoryFilters,
              showUncategorized: this.hasUncategorizedSponsorSection
            }
          });
        }
        return out;
      }

      const sections = [];
      let sponsorsAdded = false;
      this.lobbyElements.forEach((el, index) => {
        if (!el) return;
        const id = el.id;
        const type = el.type;

        // Hero/header → rendered separately at the top.
        if (
          id === 'banner' ||
          id === 'header' ||
          type === moduleType.BANNER ||
          type === moduleType.HEADER
        ) {
          return;
        }
        // About / Information
        if (id === 'information' || type === moduleType.INFO_PAGE) {
          if (f.lobbyFAQPages !== false) {
            sections.push({
              key: id || 'information',
              component: 'LobbyFaq',
              props: { options: el.options || {} }
            });
          }
          return;
        }
        // Featured speakers
        if (id === 'speakers' || type === moduleType.SPEAKER) {
          if (f.lobbySpeakers !== false) {
            sections.push({
              key: id || 'speakers',
              component: 'LobbySpeakers',
              props: { options: el.options || {} }
            });
          }
          return;
        }
        // Top sponsors → one section per module, at the position the
        // admin placed it. Not gated on `lobbySponsors`: the API derives
        // that flag from the regular Sponsors module only, so gating on it
        // hid Top sponsors whenever no Sponsors module was added.
        if (
          id === 'topSponsors' ||
          id === 'topsponsors' ||
          type === moduleType.TOP_SPONSORS
        ) {
          sections.push({
            key: `top-sponsors-${id || index}`,
            component: 'SponsorsMarquee',
            props: { topSponsorElement: el }
          });
          return;
        }
        // Sponsors → one unified marquee at the first sponsor element's
        // position. The admin's category sections render as labelled
        // groups inside that single card (no repeated card chrome), with
        // uncategorized sponsors shown flat on top.
        if (id === 'sponsors' || type === moduleType.SPONSOR) {
          if (!sponsorsAdded && f.lobbySponsors !== false) {
            sponsorsAdded = true;
            sections.push({
              key: 'sponsors',
              component: 'SponsorsMarquee',
              props: {
                label: this.sponsorsSectionLabel,
                categoryFilters: this.sponsorCategoryFilters,
                showUncategorized: this.hasUncategorizedSponsorSection,
                showTopSponsors: false
              }
            });
          }
          return;
        }
        // Agenda — rendered where the admin placed it in the lobby builder.
        // The LobbyAgenda wrapper gates internally on features.lobbyAgenda,
        // so that flag remains the single source of truth for visibility.
        if (id === 'agenda' || type === moduleType.AGENDA) {
          sections.push({
            key: id || 'agenda',
            component: 'LobbyAgenda',
            props: { options: el.options || {} }
          });
          return;
        }

        // Any other custom admin module → map via the component list.
        const component =
          el.component ||
          componentList.find(
            (y) => y.category === el.category && y.type === el.type
          )?.component;
        if (component) {
          sections.push({
            key: id || `el-${type}`,
            component,
            props: { options: el.options || {}, id: id }
          });
        }
      });
      return sections;
    }
  },
  watch: {
    // Redirect away from /lobby whenever the admin's "Lobby" toggle
    // flips off — covers both the initial load and a live admin
    // change (e.g. switching the event from Digital → Web App) — OR
    // whenever the current user is attending physically (the lobby is
    // hidden for them regardless of the admin toggle).
    isLobbyEnabled: {
      immediate: true,
      handler(enabled) {
        if (
          (enabled === false || this.isPhysicalAttendee) &&
          this.hasFullEventDetails
        ) {
          this.redirectAwayFromLobby();
        }
      }
    },
    hasFullEventDetails: {
      immediate: true,
      handler(loaded) {
        if (
          loaded &&
          (this.isLobbyEnabled === false || this.isPhysicalAttendee)
        ) {
          this.redirectAwayFromLobby();
        }
      }
    },
    // `isPhysicalAttendee` reads the participation from localStorage keyed
    // by the user id, so on a hard refresh it only resolves correctly once
    // the profile has loaded. The watchers above may fire (immediate)
    // before that, seeing the `guest` key and missing a physical attendee.
    // This computed re-evaluates when the profile loads, so a physical
    // attendee is still bounced off the lobby the moment we know who they
    // are.
    isPhysicalAttendee: {
      immediate: true,
      handler(isPhysical) {
        if (isPhysical && this.hasFullEventDetails) {
          this.redirectAwayFromLobby();
        }
      }
    }
  },
  methods: {
    redirectAwayFromLobby() {
      // Lobby is the "event info" surface. When admin turns it off
      // we want users to land on the program page (the agenda) rather
      // than the legacy stage hub. Stage stays available via its own
      // route — we just don't make it the default landing target.
      const next = 'program';
      if (this.$route && this.$route.name === next) return;
      this.$router.replace({ name: next }).catch(() => {});
    }
  }
};
</script>

<style scoped lang="scss">
.lobby-wrapper {
  position: relative;
  background-color: var(--background-color);
  font-family: 'Poppins', sans-serif;
  font-size: 14px;

  // Mobile: the bottom nav floats fixed over the page. `body` already
  // reserves 80px, but the nav covers most of it, leaving the last
  // section flush against the bar. Add breathing room so the final
  // card clears the nav with comfortable white space below it.
  @media (max-width: 640px) {
    padding-bottom: 48px;
  }
}

/* Badge + Voucher: side-by-side through tablet, stack only on small phones */
.badge-voucher-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
.avatar {
  object-fit: cover;
  text-align: center;
}
.full-page-loader {
  position: absolute;
  top: calc(50% - 96px);
  left: calc(50% - 96px);
  text-align: center;
}

.full-page-loader > img {
  height: 192px;
  width: 192px;
  animation: 1.8s infinite heartbeat;
  display: block;
  margin-bottom: 10px;
}

@keyframes heartbeat {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.05);
  }
  50% {
    transform: scale(1);
  }
  75% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
</style>
